import { useTranslation } from 'react-i18next';
import { useNow } from '../../shared/lib/useNow';
import type { Quota, TrafficPeriod } from '../../shared/api/types';

const DAY_MS = 86_400_000;

/** Обратный отсчёт «чч:мм» до сброса суточного лимита. */
function formatHM(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export interface QuotaResetInfo {
  period: TrafficPeriod;
  /** Момент сброса для заголовка «…до {date}»: время (сутки) или дата (месяц). */
  resetDate: string;
  /** Готовая фраза сброса: таймер (сутки) или дни с корректным окончанием (месяц). */
  resetLabel: string;
}

/**
 * Живой текст сброса трафик-лимита в зависимости от типа периода (promt-80 §1/§2):
 * суточный — обратный отсчёт «чч:мм»; месячный — количество дней с русской плюрализацией.
 */
export function useQuotaResetInfo(quota?: Quota): QuotaResetInfo {
  const { t } = useTranslation();
  const now = useNow(1000);

  const period = quota?.traffic.period ?? 'month';
  const resetAtMs = quota?.traffic.resetAt ? new Date(quota.traffic.resetAt).getTime() : null;
  const remainingMs = resetAtMs ? Math.max(0, resetAtMs - now) : 0;
  const days = Math.max(1, Math.ceil(remainingMs / DAY_MS));

  const resetDate = resetAtMs
    ? new Date(resetAtMs).toLocaleString(
        undefined,
        period === 'day'
          ? { hour: '2-digit', minute: '2-digit' }
          : { day: 'numeric', month: 'long' },
      )
    : '—';

  const resetLabel =
    period === 'day'
      ? t('quota.resetInTimer', { timer: formatHM(remainingMs) })
      : t('quota.resetInDays', { count: days });

  return { period, resetDate, resetLabel };
}
